import React, { useState, useEffect } from "react";
import { profileApi } from "../../../api/profileApi.ts";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "../../../components/ui/card";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { User, Mail, Phone, ShieldCheck, CreditCard, Ship, CheckCircle, Calendar, Edit3, Eye } from "lucide-react";
import userNotFound from "../../../assets/no-user-found.png";

const AdminProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Editable fields state
  const [form, setForm] = useState({
    name: '',
    email: '',
    mobileNumber: '',
    role: '',
    indianCdc: '',
    aadhaarNumber: '',
    currentShipName: '',
    currentShipImo: ''
  });
  const [isDirty, setIsDirty] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState(null);

  useEffect(() => {
    async function fetchProfile() {
      setLoading(true);
      setError(null);
      try {
        const data = await profileApi.fetchProfile();
        setProfile({...data, profileImage: data.profileImage ? import.meta.env.VITE_CDN_LINK + data.profileImage : "", seafarerSid: data.seafarerSid ? import.meta.env.VITE_CDN_LINK + data.seafarerSid : ""});
        setForm({
          name: data.name || '',
          email: data.email || '',
          mobileNumber: data.mobileNumber || '',
          role: data.role || '',
          indianCdc: data.indianCdc || '',
          aadhaarNumber: data.aadhaarNumber || '',
          currentShipName: data.currentShipName || '',
          currentShipImo: data.currentShipImo || ''
        });
        setIsDirty(false);
      } catch (err) {
        setError(err.message || "Failed to load profile");
      } finally {
        setLoading(false);
      }
    }
    fetchProfile();
  }, []);


  if (loading) {
    return (
      <div className="flex items-center justify-center h-full pb-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-36 w-36 border-b-2 !border-[var(--primary-font)] mx-auto mb-8"></div>
          <p className="text-[var(--primary-font)] text-2xl font-semibold">Loading profile...</p>
        </div>
      </div>
    );
  }
  
  if (error || !profile) {
    return (
      <div className="flex items-center justify-center h-full pb-20">
        <div className="text-center">
          <img src={userNotFound} alt="User Not Found" className="h-[20rem] mx-auto" />
          <Badge variant="gray" className="font-bold text-xl text-gray-500 px-10 mr-8">{error || "Profile not found."}</Badge>
        </div>
      </div>
    );
  }

  const user = profile || {};

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      setIsDirty(
        Object.keys(updated).some(
          (key) => updated[key] !== (user[key] || '')
        )
      );
      return updated;
    });
  };

  // Update Profile handler
  const handleUpdateProfile = async () => {
    setUpdateLoading(true);
    setUpdateSuccess(false);
    setUpdateError(null);
    try {
      const changedFields = {};
      Object.keys(form).forEach((key) => {
        if (form[key] !== (user[key] || '')) {
          changedFields[key] = form[key];
        }
      });
      if (Object.keys(changedFields).length === 0) {
        setUpdateLoading(false);
        return;
      }
      // remove phone field
      const updated = await profileApi.updateProfile(changedFields);
      if (updated) {
        setProfile({...updated, profileImage: updated.profileImage ? import.meta.env.VITE_CDN_LINK + updated.profileImage : "", seafarerSid: updated.seafarerSid ? import.meta.env.VITE_CDN_LINK + updated.seafarerSid : ""});
        setUpdateSuccess(true);
        setIsDirty(false);
        setIsEditMode(false);
        setTimeout(() => setUpdateSuccess(false), 2000);
      } else {
        setUpdateError("Failed to update profile");
      }
    } catch (err) {
      setUpdateError(err.message || "Failed to update profile");
    } finally {
      setIsDirty(false);
      setUpdateLoading(false);
    }
  };

  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
    if (!isEditMode) {
      // Reset form when entering edit mode
      setForm({
        name: user.name || '',
        email: user.email || '',
        mobileNumber: user.mobileNumber || '',
        role: user.role || '',
        indianCdc: user.indianCdc || '',
        aadhaarNumber: user.aadhaarNumber || '',
        currentShipName: user.currentShipName || '',
        currentShipImo: user.currentShipImo || ''
      });
      setIsDirty(false);
    }
  };

  return (
    <div className=" mx-auto p-6 space-y-6">
      <div className="bg-white rounded-lg shadow-sm border">
        {/* Header Section */}
        <div className="p-8 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-6">
              <div className="flex-shrink-0">
                <div className="w-24 h-24 bg-gray-200 rounded-lg overflow-hidden">
                  {user.profileImage ? (
                    <img 
                      src={user.profileImage}
                      alt="Profile" 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200">
                      <User className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1">
                <div className="mb-2">
                  {isEditMode ? (
                    <Input 
                      name="name" 
                      value={form.name} 
                      onChange={handleChange}
                      className="text-2xl font-bold border-0 p-0 h-auto focus-visible:ring-0 bg-transparent"
                    />
                  ) : (
                    <h1 className="text-2xl font-bold text-gray-900">{user.name || "Admin User"}</h1>
                  )}
                  <p className="text-lg text-gray-600 mt-1">
                    {user.role ? user.role.replace("_", " ").replace(/\b\w/g, l => l.toUpperCase()) : "Admin"}
                  </p>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {isEditMode ? (
                        <Input 
                          name="email" 
                          value={form.email} 
                          disabled
                          className="border-0 p-0 h-auto focus-visible:ring-0 bg-transparent text-sm"
                        />
                      ) : (
                        user.email || "emailemail@email.com"
                      )}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="font-medium">Gender:</span> 
                      <span className="font-semibold">{user.gender || "Male"}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="font-medium">Age:</span> 
                      <span className="font-semibold">{user.age || "40"}</span>
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {user.emailVerified && (
                      <Badge variant="secondary" className="text-xs px-2 py-1 bg-green-100 text-green-800 border-green-200">
                        <CheckCircle className="h-3 w-3 mr-1 inline" /> Verified
                      </Badge>
                    )}
                    {user.lastLogin && (
                      <span className="text-xs text-gray-500">Last login: {new Date(user.lastLogin).toLocaleString()}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            
            <Button
              onClick={toggleEditMode}
              variant="outline"
              className="flex items-center gap-2 hover:bg-[var(--primary-font)] hover:text-white transition-all"
            >
              {isEditMode ? (
                <>
                  <Eye className="h-4 w-4" />
                  View Profile
                </>
              ) : (
                <>
                  <Edit3 className="h-4 w-4" />
                  Edit Profile
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Additional Details Section */}
        <div className="p-8">
          <h2 className="text-xl font-semibold text-[var(--primary-font)] mb-6">Additional Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Aadhaar Number */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Aadhaar Number</label>
              {isEditMode ? (
                <Input 
                  name="aadhaarNumber" 
                  value={form.aadhaarNumber} 
                  onChange={handleChange}
                  className="bg-gray-50 border-gray-200"
                />
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm">
                  {user.aadhaarNumber || "Not provided"}
                </div>
              )}
            </div>

            {/* Current Ship Name */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Last/Current Ship's Name</label>
              {isEditMode ? (
                <Input 
                  name="currentShipName" 
                  value={form.currentShipName} 
                  onChange={handleChange}
                  className="bg-gray-50 border-gray-200"
                />
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm">
                  {user.currentShipName || "Not provided"}
                </div>
              )}
            </div>

            {/* IMO Number */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">IMO Number</label>
              {isEditMode ? (
                <Input 
                  name="currentShipImo" 
                  value={form.currentShipImo} 
                  onChange={handleChange}
                  className="bg-gray-50 border-gray-200"
                />
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm">
                  {user.currentShipImo || "Not provided"}
                </div>
              )}
            </div>

            {/* SID Document */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">SID (Seafarer's Identity Document)</label>
              <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm flex items-center justify-between">
                <span>{user.seafarerSid || user.seafarerSidId ? "doc.pdf" : "Not provided"}</span>
                {(user.seafarerSid || user.seafarerSidId) && (
                  <a
                    href={user.seafarerSid}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--primary-font)] hover:text-blue-800 text-sm font-medium"
                  >
                    View
                  </a>
                )}
              </div>
            </div>

            {/* Indian CDC Number */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">INDoS Number</label>
              <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm">
                {user.indianCdc || "Not provided"}
              </div>
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Mobile Number</label>
              {isEditMode ? (
                <Input 
                  name="mobileNumber" 
                  value={form.mobileNumber} 
                  onChange={handleChange}
                  className="bg-gray-50 border-gray-200"
                />
              ) : (
                <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm">
                  {user.mobileNumber || "Not provided"}
                </div>
              )}
            </div>

            {/* Account Created */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-600">Account Created</label>
              <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-sm">
                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : "Not available"}
              </div>
            </div>
          </div>

          {/* Update Button for Edit Mode */}
          {isEditMode && (
            <div className="flex justify-end items-center gap-4 mt-8 pt-6 border-t border-gray-100">
              {updateSuccess && (
                <Badge variant="default" className="px-4 py-1 bg-green-100 text-green-800">
                  Profile updated successfully!
                </Badge>
              )}
              {updateError && (
                <Badge variant="destructive" className="px-4 py-1">
                  {updateError}
                </Badge>
              )}
              <Button
                variant="outline"
                onClick={toggleEditMode}
                className="px-6"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpdateProfile}
                disabled={!isDirty || updateLoading}
                className="bg-[var(--primary-font)] hover:bg-[var(--primary-font)]/90 text-white px-6"
              >
                {updateLoading ? 'Updating...' : 'Update Profile'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminProfile;